// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import ClassStep from "./class-step.js"
import Course from "./course.js"

const modelClassData = {
  "attributes": {
    "class_step_id": {
      "column": {
        "default": null,
        "name": "class_step_id",
        "null": false,
        "type": null
      },
      "name": "class_step_id",
      "selected_by_default": null,
      "translated": null
    },
    "course_id": {
      "column": {
        "default": null,
        "name": "course_id",
        "null": false,
        "type": null
      },
      "name": "course_id",
      "selected_by_default": null,
      "translated": null
    },
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "CourseClassStep",
  "collectionKey": "course_class_steps",
  "collectionName": "course_class_steps",
  "i18nKey": "course_class_step",
  "camelizedLower": "courseClassStep",
  "name": "CourseClassStep",
  "nameDasherized": "course-class-step",
  "pluralName": "course_class_steps",
  "ransackable_associations": [
    {
      "className": "CourseClassStep::Audit",
      "collectionName": "course_class_step_audits",
      "foreignKey": "course_class_step_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "CourseClassStepAudit",
      "through": null
    },
    {
      "className": "ClassStep",
      "collectionName": "class_steps",
      "foreignKey": "class_step_id",
      "name": "class_step",
      "macro": "belongs_to",
      "resource_name": "ClassStep",
      "through": null
    },
    {
      "className": "Course",
      "collectionName": "courses",
      "foreignKey": "course_id",
      "name": "course",
      "macro": "belongs_to",
      "resource_name": "Course",
      "through": null
    },
    {
      "className": "CourseClassStep::Audit",
      "collectionName": "course_class_step_audits",
      "foreignKey": "course_class_step_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "CourseClassStepAudit",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "class_step_id",
      "column": {
        "default": null,
        "name": "class_step_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "course_id",
      "column": {
        "default": null,
        "name": "course_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "ClassStep",
      "collectionName": "class_steps",
      "foreignKey": "class_step_id",
      "name": "class_step",
      "macro": "belongs_to",
      "resource_name": "ClassStep",
      "through": null
    },
    {
      "className": "Course",
      "collectionName": "courses",
      "foreignKey": "course_id",
      "name": "course",
      "macro": "belongs_to",
      "resource_name": "Course",
      "through": null
    }
  ],
  "paramKey": "course_class_step",
  "primaryKey": "id"
}

/** Frontend model for CourseClassStep. */
export default class CourseClassStep extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {any} */
  classStepId() {
    return this.readAttributeUnderscore("class_step_id")
  }

  /** @returns {boolean} */
  hasClassStepId() {
    const value = this.classStepId()

    return this._isPresent(value)
  }

  /** @returns {any} */
  courseId() {
    return this.readAttributeUnderscore("course_id")
  }

  /** @returns {boolean} */
  hasCourseId() {
    const value = this.courseId()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {import("./class-step.js").default | null} */
  classStep() {
    return this._readBelongsToReflection({modelClass: ClassStep, reflectionName: "class_step"})
  }

  /** @returns {Promise<import("./class-step.js").default | null>} */
  loadClassStep() {
    if (!("classStepId" in this)) throw new Error("Foreign key method wasn't defined: classStepId")

    const id = this.classStepId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "class_step",
        model: this,
        modelClass: ClassStep
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./class-step.js").default | null>} */
  ensureClassStepLoaded() {
    return this.ensureAssociationLoaded("class_step")
  }

  /** @returns {import("./course.js").default | null} */
  course() {
    return this._readBelongsToReflection({modelClass: Course, reflectionName: "course"})
  }

  /** @returns {Promise<import("./course.js").default | null>} */
  loadCourse() {
    if (!("courseId" in this)) throw new Error("Foreign key method wasn't defined: courseId")

    const id = this.courseId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "course",
        model: this,
        modelClass: Course
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./course.js").default | null>} */
  ensureCourseLoaded() {
    return this.ensureAssociationLoaded("course")
  }
}
